"use client"

import { useQuery } from "@tanstack/react-query"
import Link from "next/link"
import { useEffect } from "react"
import { toast } from "sonner"
import { getAccountOrders } from "@/api/account-orders"
import { Button } from "@/components/ui/button"
import { AccountOrdersSkeleton } from "./skeleton"

const ORDER_STATUS_LABELS: Record<string, string> = {
  cancelled: "Cancelado",
  completed: "Concluido",
  failed: "Falhou",
  on_hold: "Em espera",
  pending: "Pendente",
  processing: "Processando",
  refunded: "Reembolsado",
}

const formatOrderDate = (value: string): string => {
  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value || "-"
  }

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date)
}

const formatOrderTotal = (value: number, currency: string): string => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(value)
}

const toOrderStatusLabel = (status: string): string => {
  return ORDER_STATUS_LABELS[status] ?? status
}

export function AccountOrdersPage() {
  const ordersQuery = useQuery({
    queryKey: ["account", "orders"],
    queryFn: () => getAccountOrders(20),
    retry: false,
  })

  useEffect(() => {
    if (!ordersQuery.data) {
      return
    }

    if (ordersQuery.data.ok) {
      return
    }

    toast.error(
      ordersQuery.data.message?.trim() ||
        "Nao foi possivel carregar os pedidos da conta."
    )
  }, [ordersQuery.data])

  useEffect(() => {
    if (!ordersQuery.isError) {
      return
    }

    toast.error("Nao foi possivel carregar os pedidos da conta.")
  }, [ordersQuery.isError])

  if (ordersQuery.isPending) {
    return <AccountOrdersSkeleton />
  }

  const hasFailedState = ordersQuery.isError || !ordersQuery.data?.ok

  if (hasFailedState) {
    return (
      <section className='rounded-2xl border bg-white p-6 shadow-xs md:p-8'>
        <div className='max-w-3xl space-y-4'>
          <h1 className='font-semibold text-3xl text-foreground'>Pedidos</h1>
          <p className='text-base text-muted-foreground leading-7'>
            Nao foi possivel carregar seus pedidos agora. Tente novamente em
            instantes.
          </p>
          <Button onClick={() => ordersQuery.refetch()} type='button'>
            Tentar novamente
          </Button>
        </div>
      </section>
    )
  }

  const orders = ordersQuery.data.orders ?? []

  if (orders.length === 0) {
    return (
      <section className='rounded-2xl border bg-white p-6 shadow-xs md:p-8'>
        <div className='max-w-3xl space-y-4'>
          <h1 className='font-semibold text-3xl text-foreground'>Pedidos</h1>
          <p className='text-base text-muted-foreground leading-7'>
            Nenhum pedido foi feito ainda.
          </p>
          <Button asChild>
            <Link href='/produtos'>Ver produtos</Link>
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className='rounded-2xl border bg-white p-6 shadow-xs md:p-8'>
      <div className='space-y-6'>
        <h1 className='font-semibold text-3xl text-foreground'>Pedidos</h1>

        <div className='overflow-x-auto'>
          <table className='min-w-full border-collapse text-left text-sm'>
            <thead>
              <tr className='border-border border-b'>
                <th
                  className='px-3 py-3 font-semibold text-foreground'
                  scope='col'
                >
                  Pedido
                </th>
                <th
                  className='px-3 py-3 font-semibold text-foreground'
                  scope='col'
                >
                  Data
                </th>
                <th
                  className='px-3 py-3 font-semibold text-foreground'
                  scope='col'
                >
                  Status
                </th>
                <th
                  className='px-3 py-3 font-semibold text-foreground'
                  scope='col'
                >
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {orders.map((order) => {
                return (
                  <tr
                    className='border-border border-b last:border-b-0'
                    key={order.id}
                  >
                    <td className='px-3 py-3 font-medium text-foreground'>
                      <Link
                        className='text-primary hover:underline'
                        href={`/minha-conta/pedidos/${order.number.replace("#", "")}`}
                      >
                        {order.number}
                      </Link>
                    </td>
                    <td className='px-3 py-3 text-muted-foreground'>
                      {formatOrderDate(order.dateCreated)}
                    </td>
                    <td className='px-3 py-3 text-foreground'>
                      {toOrderStatusLabel(order.status)}
                    </td>
                    <td className='px-3 py-3 text-foreground'>
                      {formatOrderTotal(order.total, order.currency)} para{" "}
                      {order.itemCount} item(ns)
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}
